/*
 *   Copyright (c) 2021 vlinder Labs Private Limited
 *   All rights reserved.
 *   This software is proprietary of Vlinder Labs Pvt. Ltd and can not be distributed / used for any purpose outside of Vlinder Labs Pvt. Ltd without explicit written consent from the copyright owner.
 */

const is = require("is_js");

import { VApplication } from "../application";
import { GraphqlServerModule } from "@vlinder-web/graphql-server-module-react";
import {
    QR_FIELDS,
    QR_FORM_FIELDS,
} from "../pages/CreateQRCodes/config/formData";
import { getErrorMessage } from "../utils/error.message";
import { GET_GTINS } from "../pages/CreateQRCodes/graphql/getGtins.query";
import { GET_PRODUCTS } from "../pages/CreateQRCodes/graphql/getProducts.query";
import { CREATE_BATCH } from "../pages/CreateQRCodes/graphql/create-batch.mutation";
import { CREATE_BATCH_QR } from "../pages/CreateQRCodes/graphql/createBatchQR.mutation";
import { UPDATE_ASSOCIATE_BATCH } from "../pages/CreateQRCodes/graphql/updateAssociateBatch.mutation";
import { GET_TABLE_ENTRY } from "../pages/CreateQRCodes/graphql/getTableEntry.query";
import { CREATE_ASSORTMENT_BATCH } from "../pages/NewAssortmentBatches/graphql/createQrBatch.mutation";

const { LoggerModule } = require("@vlinder-web/logger-module-react");
const SERVICE_NAME = "create-qr-codes.service";
const log: any = LoggerModule.getInstance();

const getGraphQLModule = () => {
    const app = VApplication.getInstance();
    return app.getSync("services.GraphqlServerModule") as GraphqlServerModule;
};

export class CreateQRCodesService {
    name = SERVICE_NAME;
    private static instance: CreateQRCodesService;
    private constructor() {
        this.report();
    }

    static getInstance() {
        if (!this.instance) {
            this.instance = new CreateQRCodesService();
        }
        return this.instance;
    }
    async init() {}

    _getFormFields = (data: any) => {
        let _products: any[] = [];
        _products =
            data?.getProducts?.data &&
            Array.isArray(data?.getProducts?.data) &&
            data?.getProducts?.data.length > 0 &&
            data?.getProducts?.data.map((item: any, index: number) => {
                return {
                    label: item?.name,
                    value: item?._id,
                };
            });

        let result = QR_FORM_FIELDS.map((item, index) => {
            if (item?.name === "productId") {
                return {
                    ...item,
                    fieldProps: {
                        ...item?.fieldProps,
                        initialOptions: _products,
                    },
                };
            } else {
                return {
                    ...item,
                };
            }
        });
        console.log(`_products :`, _products);
        return result;
    };

    async getProducts() {
        const graphqlModule = getGraphQLModule();
        const client = graphqlModule.getClient("graphql-gateway");
        try {
            if (client) {
                console.log("getProductsCalled");
                const res = await client.query({
                    query: GET_PRODUCTS,
                    variables: {},
                    fetchPolicy: "network-only",
                });
                if (res?.data?.getProducts)
                    return {
                        fields: QR_FIELDS,
                        formFields: this._getFormFields(res?.data),
                    };
            }
        } catch (err: any) {
            getErrorMessage(err);
            // err?.graphQLErrors &&
            //     Array.isArray(err?.graphQLErrors) &&
            //     err?.graphQLErrors.length > 0 &&
            //     toast.error(err?.graphQLErrors[0]?.message);
        }
    }

    _getGtins = (data: any) => {
        let _gtins: any[] = [];
        _gtins =
            data?.getGtinsByProductID &&
            Array.isArray(data?.getGtinsByProductID) &&
            data?.getGtinsByProductID.length > 0 &&
            data?.getGtinsByProductID.map((item: any, index: number) => {
                return {
                    label: item?.gtinKey,
                    value: item?._id,
                };
            });
        console.log(`_gtins :`, _gtins);
        return _gtins;
    };

    async getGtins(productId: string) {
        const graphqlModule = getGraphQLModule();
        const client = graphqlModule.getClient("graphql-gateway");
        try {
            if (client) {
                const res = await client.query({
                    query: GET_GTINS,
                    variables: {
                        where: {
                            productId,
                        },
                    },
                    fetchPolicy: "network-only",
                });
                if (res?.data?.getGtinsByProductID) {
                    return { gtins: res?.data?.getGtinsByProductID };
                }
            }
        } catch (err: any) {
            getErrorMessage(err);
        }
    }

    async createBatchCollection(payload: any) {
        log.blue(`upload data in service:`, payload);
        const graphqlModule = getGraphQLModule();
        const client = graphqlModule.getClient("graphql-gateway");
        try {
            if (client) {
                const res = await client.mutate({
                    mutation: CREATE_BATCH,
                    variables: {
                        where: {
                            productId: payload?.productId,
                        },
                        data: {
                            name: payload?.name,
                            description: payload?.description,
                            variants: [payload?.gtinId],
                            //    creatorUser: "6193941de1204414dfad500f"
                        },
                    },
                    fetchPolicy: "no-cache",
                });
                if (res?.data?.createBatch) {
                    return res?.data?.createBatch;
                }
            }
        } catch (err: any) {
            getErrorMessage(err);
        }
    }

    async createBatchQR() {
        const graphqlModule = getGraphQLModule();
        const client = graphqlModule.getClient("graphql-gateway");
        try {
            if (client) {
                const res = await client.mutate({
                    mutation: CREATE_BATCH_QR,
                    variables: {},
                    fetchPolicy: "no-cache",
                });
                if (res?.data?.createBatchLazyBindingParent) {
                    return res?.data?.createBatchLazyBindingParent;
                }
            }
        } catch (err: any) {
           getErrorMessage(err);
        }
    }

    async updateAssociateBatch(payload: any) {
        log.info(`payload in updateAssociateBatch service`, payload)
        const graphqlModule = getGraphQLModule();
        const client = graphqlModule.getClient("graphql-gateway");
        try {
            if (client) {
                const res = await client.mutate({
                    mutation: UPDATE_ASSOCIATE_BATCH,
                    variables: {
                        batchLazyBindingParentId: payload?.batchLazyBindingParentId,
                        associate: {
                            batchId: payload?.batchId,
                            gtinId: payload?.gtinId,
                            productId: payload?.productId
                        }
                    },
                    fetchPolicy: "no-cache",
                });
                if (res?.data?.associateBatchLazyBindingParentWithProduct) {
                    const _data = res?.data?.associateBatchLazyBindingParentWithProduct;
                    return {
                        ..._data
                    };
                }
            }
        } catch (err: any) {
            getErrorMessage(err)
        }
    }

    async getAdminTableEntry(payload: string) {
        const graphqlModule = getGraphQLModule();
        const client = graphqlModule.getClient("graphql-gateway");
        try {
            if (client) {
                const res = await client.query({
                    query: GET_TABLE_ENTRY,
                    variables: {
                        batchLazyBindingParentId: payload
                    },
                    fetchPolicy: "network-only",
                });
                if (res?.data?.getBatchLazyBindingParent) {
                    const _data = res?.data?.getBatchLazyBindingParent;
                    return {
                        ..._data
                    };
                }
            }
        } catch (err: any) {
            getErrorMessage(err)
        }
    }

    async createQrBatch(payload: any) {
        const graphqlModule = getGraphQLModule();
        const client = graphqlModule.getClient("graphql-gateway");
        try {
            if (client) {
                const res = await client.mutate({
                    mutation: CREATE_ASSORTMENT_BATCH,
                    variables: {
                        data: payload?.data,
                    },
                    fetchPolicy: "no-cache",
                });
                if (res?.data?.createSerializationGroup)
                    return res?.data?.createSerializationGroup;
            }
        } catch (err: any) {
            getErrorMessage(err);
        }
    }

    private report() {
        log?.info(SERVICE_NAME, `⚡⚡⚡===================⚡⚡⚡`);
        log?.info(SERVICE_NAME, "Module Report");
        log?.info(SERVICE_NAME, "⦿ Name: " + this.name);
        log?.info(SERVICE_NAME, `⚡⚡⚡===================⚡⚡⚡`);
    }
}
